import {
  FilterContainer,
  GroupedResidentsMultiSelect,
  MultiSelect,
  RangePicker,
} from '@lmc/ui-kit'
import styled from 'styled-components'
import dayjs from 'dayjs'
import { RangeValue } from 'types/shared'
import { LogCategory, LogCategoryItem, Home } from '@lmc/ts-common'
import { useSelector } from 'react-redux'
import { ReactNode } from 'react'
import { toDosConstants } from 'locale/toDos'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import useResidentsList from 'hooks/useResidentsList'

export type BasicFiltersValue = {
  rangePicker?: RangeValue<dayjs.Dayjs>
  residents?: string[]
  categories?: string[]
  categoryItems?: string[]
}

export type BasicFiltersProps<FiltersValue extends BasicFiltersValue> = {
  value: FiltersValue
  onChange: (value: FiltersValue) => void
  testId?: string
}

export const getName = (item: { name: { first: string; last: string } }) =>
  `${item.name.first} ${item.name.last}`

const unwantedCategories = ['bodyMaps', 'incident']

const BasicFilters = ({
  value,
  onChange,
  children,
  testId,
}: BasicFiltersProps<BasicFiltersValue> & {
  children?: ReactNode
}): JSX.Element => {
  const { t } = useTranslation(NAMESPACE.TODOS)
  const { residents } = useResidentsList({})

  const handleInputChange = (newValue: Partial<BasicFiltersValue>) => {
    const updatedValue: BasicFiltersValue = {
      ...value,
      ...newValue,
    }
    onChange(updatedValue)
  }
  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative,
  )

  const {
    'log-categories': categories,
    'log-category-items': categoryItems,
  }: {
    'log-categories': LogCategory[] | undefined
    'log-category-items': LogCategoryItem[] | undefined
  } = useSelector((state: any) => state.data)

  const unwantedCategoriesIds = categories
    ?.filter((category) => unwantedCategories.includes(category.itemId))
    .map((category) => category.id)

  const categoryOptions = categories
    ?.filter((category) => !unwantedCategories.includes(category.itemId))
    .map((category) => ({
      label: category.name,
      value: category.id,
      image: (
        <CategoryImageContainer color={category.color}>
          <CategoryImage
            alt="category"
            src={category.icon as unknown as string}
          />
        </CategoryImageContainer>
      ),
    }))
    .sort((a, b) => a.label.localeCompare(b.label))

  let newCategoryItems = categoryItems?.filter((categoryItem) =>
    value?.categories?.includes(categoryItem.category),
  )

  if (!newCategoryItems?.length) {
    newCategoryItems = categoryItems
  }

  const filteredCategoryItems = newCategoryItems
    ?.filter(
      (categoryItem) => !unwantedCategoriesIds?.includes(categoryItem.category),
    )
    .sort((a, b) => {
      if (a.category === b.category) {
        return a.name.localeCompare(b.name)
      }
      return a.category - b.category
    })

  const categoryItemsOptions = (filteredCategoryItems || []).map(
    (categoryItem: LogCategoryItem) => ({
      label: categoryItem.name,
      value: categoryItem.id,
      category: categoryItem.category,
      image: (
        <CategoryImageContainer
          color={
            categories?.find(
              (category) => category.id === categoryItem.category,
            )?.color
          }
        >
          <CategoryImage
            src={categoryItem.icon as unknown as string}
            alt="categoryItem"
          />
        </CategoryImageContainer>
      ),
    }),
  )

  const sortedCategoryItemsOptions = [...categoryItemsOptions]?.sort((a, b) => {
    if (a.category === b.category) {
      return a.label.localeCompare(b.label)
    }
    return a.category.localeCompare(b.category)
  })

  // we agreed that we're not using render functions except for special cases.
  // special case here, didn't want to duplicate code and a new component does not make sense here
  const renderFilters = () => (
    <>
      <StyledRangePicker
        value={value.rangePicker}
        onChange={(rangePicker) => handleInputChange({ rangePicker })}
        placeholder={[t(toDosConstants.FROM), t(toDosConstants.TO)]}
        data-testid={
          testId
            ? `${testId}-date-range-picker`
            : 'basic-filters-date-range-picker'
        }
      />
      <MultiSelectWide
        values={value.categories}
        title="Categories"
        options={categoryOptions || []}
        hasClear
        hasSearch
        hasSelectAll
        onChange={(selectedCategories) =>
          handleInputChange({ categories: selectedCategories })
        }
        data-testid={
          testId
            ? `${testId}-categories-select`
            : 'basic-filters-categories-select'
        }
      />
      <MultiSelectWide
        values={value.categoryItems}
        title="Items"
        options={sortedCategoryItemsOptions}
        hasClear
        hasSearch
        hasSelectAll
        onChange={(selectedCategoryItems) =>
          handleInputChange({ categoryItems: selectedCategoryItems })
        }
        data-testid={
          testId ? `${testId}-items-select` : 'basic-filters-items-select'
        }
      />
      <GroupedResidentsMultiSelect
        residents={residents}
        title={preferredPeopleNominative || ''}
        values={value.residents || []}
        handleInputChange={(selectedOptions) =>
          handleInputChange({
            residents: selectedOptions as string[],
          })
        }
        data-testid={
          testId
            ? `${testId}-residents-select`
            : 'basic-filters-residents-select'
        }
      />
      {children}
    </>
  )

  return <FilterContainer title="Filters">{renderFilters()}</FilterContainer>
}

const CategoryImageContainer = styled.div<{ color?: string | undefined }>`
  background-color: ${(props) => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  overflow: 'hidden';
  width: 20px;
  height: 20px;
`
const CategoryImage = styled.img`
  width: 12px;
  height: 12px;
`

export const MultiSelectWide = styled(MultiSelect)`
  min-width: 200px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    width: 100%;
  }
` as typeof MultiSelect

const StyledRangePicker = styled(RangePicker)`
  ${({ theme }) => theme.mediaQueries.tabletDown} {
    width: 65%;
  }
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    width: 100%;
  }
`

export default BasicFilters
